// 📂 components/Client/Table/ModuleDetailPanel/MenuTableRow.tsx
import React, { useState, useEffect } from "react";
import TableInput from "../../../Forms/Input/TableInput";
import TableCheckbox from "../../../Forms/Input/TableCheckbox";
import { useMenu } from "../../../../hooks/menu/useMenu";
import { useModule } from "../../../../hooks/module/useModule";
import { useRole } from "../../../../hooks/useRole";
import { IMenuDetail } from "../../../../types/menu";
import useDebounced from "../../../../hooks/useDebounced";
import { menuGridTemplate } from "./menuTableGrid";

interface MenuTableRowProps {
  menu: IMenuDetail;
}

const MenuTableRow: React.FC<MenuTableRowProps> = ({ menu }) => {
  const { updateMenuRoles, updateMenuOrder, fetchMenusByModule } = useMenu();
  const { moduleDetail } = useModule();
  const { dropdownOptions, fetchRolesDropdown } = useRole();

  const [order, setOrder] = useState<string>(String(menu.orderIndex ?? ""));
  const [selectedRoles, setSelectedRoles] = useState<string[]>(menu.roles);

  const debouncedOrder = useDebounced(order, 500);
  // identidad = código técnico; etiqueta = nombre legible
  const roleColumns = dropdownOptions.map((r) => ({
    code: r.code ?? r.name,
    label: r.name,
  }));

  // 🚀 Cargar roles al montar
  useEffect(() => {
    fetchRolesDropdown();
  }, []);

  // 🔄 Mantener sincronización cuando cambie el menú
  useEffect(() => {
    setOrder(String(menu.orderIndex ?? ""));
    setSelectedRoles(menu.roles);
  }, [menu]);

  // 🎭 Actualizar roles
  const handleToggleRole = async (role: string) => {
    const updatedRoles = selectedRoles.includes(role)
      ? selectedRoles.filter((r) => r !== role)
      : [...selectedRoles, role];

    setSelectedRoles(updatedRoles);

    if (moduleDetail?.id && dropdownOptions.length > 0) {
      const selectedRoleIds = dropdownOptions
        .filter((r) => updatedRoles.includes(r.code ?? r.name))
        .map((r) => r.id);

      await updateMenuRoles({
        menuId: menu.id,
        roleIds: selectedRoleIds,
      });
    }
  };

  // 🔢 Actualizar orden (debounced + validación)
  useEffect(() => {
    const refreshOrder = async () => {
      if (!moduleDetail?.id) return;

      const parsed = parseInt(debouncedOrder, 10);
      if (isNaN(parsed)) return;

      if (parsed !== menu.orderIndex) {
        await updateMenuOrder({
          menuId: menu.id,
          newOrder: parsed,
        });

        await fetchMenusByModule(moduleDetail.id);
      }
    };

    refreshOrder();
  }, [debouncedOrder]);

  // 🧩 Render
  return (
    <div
      className="grid border-b border-stroke bg-white transition last:border-b-0 hover:bg-gray-1 dark:border-strokedark dark:bg-boxdark dark:hover:bg-meta-4"
      style={{
        gridTemplateColumns: menuGridTemplate(roleColumns.length),
      }}
    >
      {/* 📛 Nombre del menú */}
      <div className="flex items-center px-4 py-3 text-sm font-medium text-black dark:text-white">
        {menu.name}
      </div>

      {/* 🎭 Roles dinámicos */}
      {roleColumns.map((role) => (
        <div
          key={role.code}
          className="flex items-center justify-center py-3"
        >
          <TableCheckbox
            checked={selectedRoles.includes(role.code)}
            onChange={() => handleToggleRole(role.code)}
          />
        </div>
      ))}

      {/* 🔢 Orden editable */}
      <div className="flex items-center justify-center px-3 py-3">
        <TableInput
          type="number"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          className="w-14 text-center"
          placeholder="—"
        />
      </div>
    </div>
  );
};

export default MenuTableRow;
